/**
 * How to play — rendered from the rules **this room is actually using**, not a fixed
 * text. Everybody has played this differently, so a player checking whether the ace is
 * high must get the answer for the game in front of them. scope.md §4.3.
 */

import type { RoomRules } from '@garbage/shared';
import { DEFAULT_RULES, ROUNDS, SCORE_TABLES } from '@garbage/shared';
import { contractFor } from '@garbage/engine';
import { Rich, useContractText, useSecondsText, useT } from '../i18n.js';

export function HowToPlay({ rules, onClose }: { rules: RoomRules | null; onClose: () => void }) {
  const t = useT();
  const contractText = useContractText();
  const secondsText = useSecondsText();
  const inUse = rules ?? DEFAULT_RULES;
  const values = SCORE_TABLES[inUse.scoring];
  const time = secondsText(inUse.claimWindowMs);

  const rounds = Array.from({ length: ROUNDS }, (_, i) => {
    const round = i + 1;
    return { round, dealt: 6 + round, contract: contractText(contractFor(round, inUse)) };
  });

  return (
    <main
      data-testid="how-to-play"
      className="h-full overflow-y-auto px-5 pt-[calc(1rem+var(--pad-top))] pb-[calc(6rem+var(--pad-bottom))]"
    >
      <h1 className="mb-1 text-2xl font-black text-sun">{t('common.howToPlay')}</h1>
      <p className="mb-4 text-xs text-bone/50">{t('help.intro')}</p>

      <Section title={t('help.idea.title')}>
        <p><Rich text={t('help.idea.body', { rounds: ROUNDS })} /></p>
      </Section>

      <Section title={t('help.melds.title')}>
        <p><Rich text={t('help.melds.body')} /></p>
        <p><Rich text={t(`help.ace.${inUse.ace}`)} /></p>
        <p><Rich text={t('help.jokers')} /></p>
      </Section>

      <Section title={t('help.rounds.title')}>
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-bone/40">
              <th className="pb-1">{t('help.rounds.round')}</th>
              <th className="pb-1">{t('help.rounds.cards')}</th>
              <th className="pb-1">{t('help.rounds.need')}</th>
            </tr>
          </thead>
          <tbody>
            {rounds.map((r) => (
              <tr key={r.round} className="border-t border-white/10" data-testid={`round-${r.round}`}>
                <td className="py-1 pr-2">{r.round}</td>
                <td className="py-1 pr-2 text-bone/60">{r.dealt}</td>
                <td className="py-1 font-bold">{r.contract}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {contractFor(ROUNDS, inUse).useWholeHand && (
          <p className="mt-2"><Rich text={t('help.rounds.lastRound')} /></p>
        )}
      </Section>

      <Section title={t('help.turn.title')}>
        <p>{t('help.turn.body')}</p>
        <p className="text-bone/60">{t('help.turn.spare')}</p>
        <p><Rich text={t('help.turn.onlyAdd')} /></p>
      </Section>

      <Section title={t('help.ask.title')}>
        <p><Rich text={t('help.ask.body', { time })} /></p>
        <p>{inUse.claimPenalty === 'oneCard' ? t('help.ask.penalty') : t('help.ask.free')}</p>
        {inUse.claimsPerRound !== 'unlimited' && (
          <p><Rich text={t('help.ask.limit', { count: inUse.claimsPerRound })} /></p>
        )}
        <p><Rich text={t('help.ask.cascade', { time })} /></p>
        <p>{t('help.ask.anybody')}</p>
        <p className="text-bone/60">{t('help.ask.pileOnly')}</p>
      </Section>

      {inUse.jokerSwap !== 'off' && (
        <Section title={t('help.joker.title')}>
          <p>{inUse.jokerSwap === 'any' ? t('help.joker.any') : t('help.joker.othersOnly')}</p>
        </Section>
      )}

      <Section title={t('help.scoring.title')}>
        <ul className="list-inside list-disc">
          <li><Rich text={t('help.scoring.low', { points: values.low })} /></li>
          <li><Rich text={t('help.scoring.ten', { points: values.ten })} /></li>
          <li><Rich text={t('help.scoring.ace', { points: values.ace })} /></li>
          <li><Rich text={t('help.scoring.joker', { points: values.joker })} /></li>
        </ul>
        <p>{t(`help.bonus.${inUse.goOutBonus}`)}</p>
        <p className="font-bold text-sun">{t('help.scoring.lowest')}</p>
      </Section>

      <p className="mb-5 text-center text-[11px] text-bone/40">{t('settings.records')}</p>

      <button
        type="button"
        data-testid="close-help"
        onClick={onClose}
        className="fixed inset-x-5 bottom-[calc(1rem+var(--pad-bottom))] rounded-2xl bg-sun py-4
                   text-lg font-black text-ink shadow-lg"
      >
        {t('help.gotIt')}
      </button>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h2 className="mb-1 text-sm font-black uppercase tracking-wide text-bone/50">{title}</h2>
      <div className="flex flex-col gap-2 text-sm leading-relaxed text-bone/90">{children}</div>
    </section>
  );
}
